import React, { useState, useRef, useEffect } from 'react';
import { X, ChevronDown, Check } from 'lucide-react';
import * as api from '../lib/api';

/* ═══════════ Country Code Data ═══════════ */
const COUNTRY_CODES = [
  { code: '+1', country: 'US', flag: '🇺🇸', label: 'United States' },
  { code: '+1', country: 'CA', flag: '🇨🇦', label: 'Canada' },
  { code: '+44', country: 'GB', flag: '🇬🇧', label: 'United Kingdom' },
  { code: '+91', country: 'IN', flag: '🇮🇳', label: 'India' },
  { code: '+49', country: 'DE', flag: '🇩🇪', label: 'Germany' },
  { code: '+33', country: 'FR', flag: '🇫🇷', label: 'France' },
  { code: '+61', country: 'AU', flag: '🇦🇺', label: 'Australia' },
  { code: '+81', country: 'JP', flag: '🇯🇵', label: 'Japan' },
  { code: '+86', country: 'CN', flag: '🇨🇳', label: 'China' },
  { code: '+82', country: 'KR', flag: '🇰🇷', label: 'South Korea' },
  { code: '+55', country: 'BR', flag: '🇧🇷', label: 'Brazil' },
  { code: '+52', country: 'MX', flag: '🇲🇽', label: 'Mexico' },
  { code: '+34', country: 'ES', flag: '🇪🇸', label: 'Spain' },
  { code: '+39', country: 'IT', flag: '🇮🇹', label: 'Italy' },
  { code: '+31', country: 'NL', flag: '🇳🇱', label: 'Netherlands' },
  { code: '+46', country: 'SE', flag: '🇸🇪', label: 'Sweden' },
  { code: '+41', country: 'CH', flag: '🇨🇭', label: 'Switzerland' },
  { code: '+65', country: 'SG', flag: '🇸🇬', label: 'Singapore' },
  { code: '+971', country: 'AE', flag: '🇦🇪', label: 'UAE' },
  { code: '+972', country: 'IL', flag: '🇮🇱', label: 'Israel' },
  { code: '+48', country: 'PL', flag: '🇵🇱', label: 'Poland' },
  { code: '+47', country: 'NO', flag: '🇳🇴', label: 'Norway' },
  { code: '+45', country: 'DK', flag: '🇩🇰', label: 'Denmark' },
  { code: '+353', country: 'IE', flag: '🇮🇪', label: 'Ireland' },
  { code: '+64', country: 'NZ', flag: '🇳🇿', label: 'New Zealand' },
  { code: '+63', country: 'PH', flag: '🇵🇭', label: 'Philippines' },
  { code: '+234', country: 'NG', flag: '🇳🇬', label: 'Nigeria' },
  { code: '+254', country: 'KE', flag: '🇰🇪', label: 'Kenya' },
  { code: '+27', country: 'ZA', flag: '🇿🇦', label: 'South Africa' },
  { code: '+62', country: 'ID', flag: '🇮🇩', label: 'Indonesia' },
];

/* ═══════════ Country Code Dropdown ═══════════ */
const CountryCodeSelect = ({ selected, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedCountry = COUNTRY_CODES.find(c => c.country === selected) || COUNTRY_CODES[0];

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 px-2 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors rounded-l-md border-r border-gray-200 h-full min-w-[80px]"
      >
        <span className="text-lg leading-none">{selectedCountry.flag}</span>
        <span className="text-gray-400 text-xs">-</span>
        <span className="text-sm">{selectedCountry.code}</span>
        <ChevronDown className="w-3 h-3 text-gray-400 ml-0.5" />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg w-64 max-h-60 overflow-y-auto">
          {COUNTRY_CODES.map((c) => (
            <button
              key={`${c.country}-${c.code}`}
              type="button"
              onClick={() => { onChange(c.country); setOpen(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${
                selected === c.country ? 'bg-[#F1F5E6] text-[#0A3900]' : 'text-gray-700'
              }`}
            >
              <span className="text-lg">{c.flag}</span>
              <span className="flex-1 text-left">{c.label}</span>
              <span className="text-gray-400 text-xs">{c.code}</span>
              {selected === c.country && <Check className="w-4 h-4 text-[#0A3900]" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ═══════════ Application Start Modal ═══════════ */
const ApplicationStartModal = ({ isOpen, onClose, job, onStartApplication }) => {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneCountry, setPhoneCountry] = useState('US');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [noLinkedin, setNoLinkedin] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const modalRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose();
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const validatePhone = (number) => {
    const digitsOnly = number.replace(/\D/g, '');
    return digitsOnly.length >= 7;
  };

  const validateEmail = (emailStr) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailStr);
  };

  const validate = () => {
    const newErrors = {};
    if (!fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!email.trim()) newErrors.email = 'Email is required';
    else if (!validateEmail(email)) newErrors.email = 'Invalid email address';
    if (!phoneNumber.trim()) newErrors.phone = 'Phone number is required';
    else if (!validatePhone(phoneNumber)) newErrors.phone = 'Invalid phone number';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const selectedCountry = COUNTRY_CODES.find(c => c.country === phoneCountry) || COUNTRY_CODES[0];
      
      const signupData = {
        job_id: job.id || job.job_id,
        full_name: fullName.trim(),
        email: email.trim(),
        phone_country_code: selectedCountry.code,
        phone_number: phoneNumber.trim(),
        linkedin_url: noLinkedin ? '' : linkedinUrl.trim(),
        no_linkedin: noLinkedin,
      };

      await api.createApplicationSignup(signupData);
      
      // Call the callback to navigate to application flow
      if (onStartApplication) {
        onStartApplication(signupData);
      }
    } catch (err) {
      setErrors({ submit: err.message || 'Failed to start application' });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !job) return null;

  const pay = job.payRange || job.pay_range || { min: 0, max: 0 };
  const payStr = pay.min === pay.max ? `$${pay.min}` : `$${pay.min}-$${pay.max}`;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" />

      {/* Modal */}
      <div
        ref={modalRef}
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[480px] mx-4 overflow-hidden max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-200"
        data-testid="application-start-modal"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100 z-10"
          data-testid="modal-close-btn"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Content */}
        <div className="px-7 pt-6 pb-7">
          {/* Job Title & Pay */}
          <div className="mb-6">
            <h2 className="text-lg font-bold text-gray-900 pr-8" data-testid="modal-job-title">
              {job.title}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {payStr} per hour
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Full name<span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => { setFullName(e.target.value); setErrors(prev => ({ ...prev, fullName: undefined })); }}
                placeholder="Enter your full name"
                className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none transition-colors ${
                  errors.fullName
                    ? 'border-red-300 focus:border-red-500 focus:ring-1 focus:ring-red-200'
                    : 'border-gray-200 focus:border-[#3f8033] focus:ring-1 focus:ring-[#E0E8D0]'
                }`}
                data-testid="input-full-name"
              />
              {errors.fullName && <p className="text-xs text-red-500 mt-1">{errors.fullName}</p>}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Email<span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setErrors(prev => ({ ...prev, email: undefined })); }}
                placeholder="you@email.com"
                className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none transition-colors ${
                  errors.email
                    ? 'border-red-300 focus:border-red-500 focus:ring-1 focus:ring-red-200'
                    : 'border-gray-200 focus:border-[#3f8033] focus:ring-1 focus:ring-[#E0E8D0]'
                }`}
                data-testid="input-email"
              />
              {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Phone<span className="text-red-500">*</span>
              </label>
              <div className={`flex items-center border rounded-lg overflow-hidden transition-colors ${
                errors.phone
                  ? 'border-red-300 focus-within:border-red-500 focus-within:ring-1 focus-within:ring-red-200'
                  : 'border-gray-200 focus-within:border-[#3f8033] focus-within:ring-1 focus-within:ring-[#E0E8D0]'
              }`}>
                <CountryCodeSelect
                  selected={phoneCountry}
                  onChange={setPhoneCountry}
                />
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => { setPhoneNumber(e.target.value); setErrors(prev => ({ ...prev, phone: undefined })); }}
                  placeholder="Phone number"
                  className="flex-1 px-3 py-2.5 text-sm focus:outline-none bg-transparent"
                  data-testid="input-phone"
                />
              </div>
              {errors.phone && <p className="text-xs text-red-500 mt-1">{errors.phone}</p>}
            </div>

            {/* LinkedIn URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                LinkedIn URL
              </label>
              <input
                type="url"
                value={linkedinUrl}
                onChange={(e) => setLinkedinUrl(e.target.value)}
                placeholder="https://linkedin.com/in/yourprofile"
                disabled={noLinkedin}
                className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none transition-colors ${
                  noLinkedin
                    ? 'bg-gray-50 border-gray-100 text-gray-400 cursor-not-allowed'
                    : 'border-gray-200 focus:border-[#3f8033] focus:ring-1 focus:ring-[#E0E8D0]'
                }`}
                data-testid="input-linkedin"
              />
              {/* I don't have a LinkedIn checkbox */}
              <label className="flex items-center gap-2 mt-2 cursor-pointer group">
                <div
                  onClick={() => { setNoLinkedin(!noLinkedin); if (!noLinkedin) setLinkedinUrl(''); }}
                  className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors cursor-pointer ${
                    noLinkedin
                      ? 'border-transparent'
                      : 'border-gray-300 group-hover:border-gray-400'
                  }`}
                  style={noLinkedin ? { backgroundColor: '#082900' } : {}}
                  data-testid="checkbox-no-linkedin"
                >
                  {noLinkedin && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                </div>
                <span className="text-sm text-gray-600 select-none">I don't have a LinkedIn</span>
              </label>
            </div>

            {/* Error message */}
            {errors.submit && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{errors.submit}</p>
              </div>
            )}

            {/* Start Application Button */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 rounded-xl font-semibold text-sm transition-all duration-200 mt-2 text-white hover:opacity-90"
              style={{ backgroundColor: submitting ? '#6b8a5a' : '#082900' }}
              data-testid="submit-start-application"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Starting...
                </span>
              ) : (
                'Start Application'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ApplicationStartModal;
